import type { DialogueDefInput } from '../schemas/progressionDefs';

/**
 * Every dialogue in the MVP (content-schemas §5.2, story-dialogue §3, §7, §8, §11). Node text keys are the
 * story-dialogue keys verbatim. Node ids are n1..nN in reading order; a node continues to the next one unless
 * it has choices or an explicit `next`.
 *
 * Naming: chapters `dlg_ch<N>`; NPC lines `dlg_<npcId>_<context>_<n>` (context = greet_lv0..3, idle, daily,
 * like_place, visit_home); level-up scenes `dlg_<npcId>_lvup<L>`; first home visit `dlg_<npcId>_visit_home_first`;
 * Boss Cat reactions `dlg_cat_<context>_<n>`.
 */
type Node = DialogueDefInput['nodes'][number];
type Speaker = Node['speaker'];
type Extra = Omit<Node, 'id' | 'speaker' | 'textKey'>;
type Line = readonly [speaker: Speaker, textKey: string, extra?: Extra];
type DialogueId = DialogueDefInput['id'];
type HumanNpc = 'npc_lan' | 'npc_bao' | 'npc_mit';

type Expression = NonNullable<Node['expression']>;

/**
 * Portrait expression per line (style-guide §11.3), chosen from the text: anything not listed is neutral.
 * Grandma's letters and notes show her small photo with the same expressions.
 */
const EXPRESSIONS: Partial<Record<string, Expression>> = {
  // Chapters
  'story.ch0.3': 'happy',
  'story.ch1.3': 'happy',
  'story.ch2.5': 'surprised',
  'story.ch2.6': 'happy',
  'story.ch2.7': 'happy',
  'story.ch2.8': 'sweat',
  'story.ch2.9': 'surprised',
  'story.ch2.10': 'happy',
  'story.ch2.11': 'meow',
  'story.ch3.2': 'meow',
  'story.ch3.5': 'happy',
  'story.ch3.6': 'sweat',
  'story.ch3.8': 'thinking',
  'story.ch4.4': 'happy',
  'story.ch4.5': 'happy',
  'story.ch4.7': 'happy',
  'story.ch4.8': 'sweat',
  'story.ch4.10': 'surprised',
  'story.ch5.2': 'happy',
  'story.ch5.3': 'thinking',
  'story.ch5.4': 'surprised',
  'story.ch5.5': 'happy',
  'story.ch5.6': 'happy',
  'story.ch5.7': 'meow',
  'story.ch6.2': 'meow',
  'story.ch6.5': 'happy',
  'story.ch6.6': 'surprised',
  'story.ch6.7': 'happy',
  'story.ch6.8': 'sweat',
  'story.ch6.9': 'happy',
  'story.ch7.2': 'happy',
  'story.ch7.5': 'happy',
  'story.ch7.6': 'sweat',
  'story.ch7.7': 'happy',
  'story.ch7.9': 'thinking',
  'story.ch7.10': 'happy',
  'story.ch8.2': 'happy',
  'story.ch8.3': 'thinking',
  'story.ch8.5': 'happy',
  'story.ch8.6': 'sweat',
  'story.ch8.7': 'surprised',
  'story.ch8.8': 'meow',
  // Cô Lan
  'npc.npc_lan.greet_lv0.1': 'happy',
  'npc.npc_lan.greet_lv0.3': 'happy',
  'npc.npc_lan.greet_lv1.1': 'happy',
  'npc.npc_lan.greet_lv1.2': 'happy',
  'npc.npc_lan.greet_lv1.3': 'surprised',
  'npc.npc_lan.greet_lv2.1': 'happy',
  'npc.npc_lan.greet_lv2.3': 'happy',
  'npc.npc_lan.greet_lv3.1': 'happy',
  'npc.npc_lan.greet_lv3.2': 'sweat',
  'npc.npc_lan.greet_lv3.3': 'happy',
  'npc.npc_lan.idle.1': 'happy',
  'npc.npc_lan.idle.2': 'sweat',
  'npc.npc_lan.idle.4': 'surprised',
  'npc.npc_lan.idle.5': 'happy',
  'npc.npc_lan.idle.7': 'happy',
  'npc.npc_lan.idle.9': 'sweat',
  'npc.npc_lan.daily.1': 'surprised',
  'npc.npc_lan.daily.2': 'happy',
  'npc.npc_lan.daily.3': 'happy',
  'npc.npc_lan.daily.5': 'sweat',
  'npc.npc_lan.daily.r1': 'happy',
  'npc.npc_lan.daily.r2': 'happy',
  'npc.npc_lan.lvup1.1': 'happy',
  'npc.npc_lan.lvup2.1': 'happy',
  'npc.npc_lan.lvup2.2': 'happy',
  'npc.npc_lan.lvup3.2': 'happy',
  'npc.npc_lan.lvup3.3': 'sweat',
  'npc.npc_lan.visit_home.1': 'happy',
  'npc.npc_lan.visit_home.3': 'sweat',
  'npc.npc_lan.visit_home.5': 'surprised',
  'npc.npc_lan.like_place.1': 'happy',
  'npc.npc_lan.like_place.2': 'happy',
  'npc.npc_lan.like_place.3': 'surprised',
  // Anh Bảo
  'npc.npc_bao.greet_lv0.1': 'sweat',
  'npc.npc_bao.greet_lv0.2': 'sleepy',
  'npc.npc_bao.greet_lv0.3': 'sweat',
  'npc.npc_bao.greet_lv1.1': 'sleepy',
  'npc.npc_bao.greet_lv1.2': 'happy',
  'npc.npc_bao.greet_lv2.1': 'happy',
  'npc.npc_bao.greet_lv2.2': 'happy',
  'npc.npc_bao.greet_lv2.3': 'sweat',
  'npc.npc_bao.greet_lv3.1': 'happy',
  'npc.npc_bao.greet_lv3.2': 'sweat',
  'npc.npc_bao.greet_lv3.3': 'happy',
  'npc.npc_bao.idle.1': 'surprised',
  'npc.npc_bao.idle.2': 'sleepy',
  'npc.npc_bao.idle.3': 'sleepy',
  'npc.npc_bao.idle.5': 'sleepy',
  'npc.npc_bao.idle.7': 'sweat',
  'npc.npc_bao.idle.8': 'sweat',
  'npc.npc_bao.idle.9': 'happy',
  'npc.npc_bao.daily.1': 'sleepy',
  'npc.npc_bao.daily.2': 'sleepy',
  'npc.npc_bao.daily.4': 'happy',
  'npc.npc_bao.daily.5': 'sweat',
  'npc.npc_bao.lvup1.1': 'happy',
  'npc.npc_bao.lvup2.1': 'happy',
  'npc.npc_bao.lvup2.2': 'sweat',
  'npc.npc_bao.lvup3.1': 'sweat',
  'npc.npc_bao.lvup3.3': 'happy',
  'npc.npc_bao.like_place.1': 'happy',
  'npc.npc_bao.like_place.3': 'happy',
  // Bé Mít
  'npc.npc_mit.greet_lv0.1': 'happy',
  'npc.npc_mit.greet_lv0.2': 'surprised',
  'npc.npc_mit.greet_lv0.3': 'wink',
  'npc.npc_mit.greet_lv1.1': 'happy',
  'npc.npc_mit.greet_lv1.2': 'surprised',
  'npc.npc_mit.greet_lv1.3': 'happy',
  'npc.npc_mit.greet_lv2.1': 'wink',
  'npc.npc_mit.greet_lv2.2': 'wink',
  'npc.npc_mit.greet_lv2.3': 'happy',
  'npc.npc_mit.greet_lv3.1': 'happy',
  'npc.npc_mit.greet_lv3.2': 'happy',
  'npc.npc_mit.idle.1': 'surprised',
  'npc.npc_mit.idle.2': 'surprised',
  'npc.npc_mit.idle.4': 'wink',
  'npc.npc_mit.idle.6': 'wink',
  'npc.npc_mit.idle.7': 'surprised',
  'npc.npc_mit.idle.9': 'surprised',
  'npc.npc_mit.daily.1': 'wink',
  'npc.npc_mit.daily.2': 'happy',
  'npc.npc_mit.daily.5': 'surprised',
  'npc.npc_mit.lvup1.1': 'happy',
  'npc.npc_mit.lvup2.1': 'happy',
  'npc.npc_mit.lvup2.2': 'wink',
  'npc.npc_mit.lvup3.1': 'happy',
  'npc.npc_mit.lvup3.2': 'happy',
  'npc.npc_mit.lvup3.3': 'wink',
  'npc.npc_mit.visit_home.1': 'happy',
  'npc.npc_mit.visit_home.2': 'meow',
  'npc.npc_mit.visit_home.4': 'surprised',
  'npc.npc_mit.visit_home.5': 'surprised',
  'npc.npc_mit.visit_home.7': 'wink',
  'npc.npc_mit.like_place.1': 'happy',
  'npc.npc_mit.like_place.2': 'happy',
  'npc.npc_mit.like_place.3': 'surprised',
  // Sếp Mèo
  'cat.tap.2': 'meow',
  'cat.tap.3': 'surprised',
  'cat.tap.4': 'meow',
  'cat.tap.6': 'meow',
  'cat.tap.7': 'meow',
  'cat.tap.8': 'happy',
  'cat.tap.9': 'happy',
  'cat.tap.10': 'surprised',
  'cat.unblock.2': 'meow',
  'cat.pate.2': 'happy',
  'cat.lvup1.2': 'happy',
  'cat.lvup2.2': 'meow',
};

/** Linear dialogue: node i flows into node i+1; `extra` may add choices, effects, conditions or override next. */
function dialogue(id: DialogueId, lines: readonly Line[]): DialogueDefInput {
  return {
    id,
    nodes: lines.map(([speaker, textKey, extra], i) =>
      withExpression({
        id: `n${String(i + 1)}`,
        speaker,
        textKey,
        next: i + 1 < lines.length ? `n${String(i + 2)}` : null,
        ...extra,
      }),
    ),
  };
}

/** Adds the line's expression from the table unless the node sets its own. */
function withExpression(node: Node): Node {
  const expression = node.expression ?? EXPRESSIONS[node.textKey];
  return expression ? { ...node, expression } : node;
}

const range = (count: number): number[] => Array.from({ length: count }, (_, i) => i + 1);

/** One single-line dialogue per `npc.<npc>.<context>.<n>` line. */
function npcLines(
  npc: HumanNpc,
  context: string,
  count: number,
  skip: readonly number[] = [],
): DialogueDefInput[] {
  return range(count)
    .filter((n) => !skip.includes(n))
    .map((n) =>
      dialogue(`dlg_${npc}_${context}_${String(n)}`, [[npc, `npc.${npc}.${context}.${String(n)}`]]),
    );
}

/** "Meo" bubble followed by the narrator's translation caption. */
const catWithCaption = (id: DialogueId, context: string, n: number): DialogueDefInput =>
  dialogue(id, [
    ['npc_boss_cat', `cat.${context}.${String(n)}`],
    ['narrator', `cat.${context}_caption.${String(n)}`],
  ]);

const catNarration = (id: DialogueId, key: string): DialogueDefInput => dialogue(id, [['narrator', key]]);

const twoChoices = (chapter: string, next: string, c1Effects?: Extra['effects']): Extra => ({
  choices: [
    { textKey: `story.${chapter}.c1`, next, ...(c1Effects ? { effects: c1Effects } : {}) },
    { textKey: `story.${chapter}.c2`, next },
  ],
});

// ---- Chapters (story-dialogue §3) --------------------------------------------------------------------------

const CHAPTER_DIALOGUES: DialogueDefInput[] = [
  dialogue('dlg_ch0', [
    ['narrator', 'story.ch0.1'],
    ['narrator', 'story.ch0.2'],
    ['grandma_letter', 'story.ch0.3'],
    ['grandma_letter', 'story.ch0.4'],
    ['narrator', 'story.ch0.5'],
    ['narrator', 'story.ch0.6'],
  ]),
  dialogue('dlg_ch1', [
    ['narrator', 'story.ch1.1'],
    ['narrator', 'story.ch1.2'],
    ['grandma_letter', 'story.ch1.3'],
    ['grandma_letter', 'story.ch1.4'],
    // The two choices are for feeling only (no effects).
    ['narrator', 'story.ch1.5', twoChoices('ch1', 'n6')],
    ['narrator', 'story.ch1.6'],
  ]),
  dialogue('dlg_ch2', [
    ['narrator', 'story.ch2.1'],
    ['narrator', 'story.ch2.2'],
    ['npc_boss_cat', 'story.ch2.3'],
    ['narrator', 'story.ch2.4'],
    ['npc_lan', 'story.ch2.5'],
    ['npc_lan', 'story.ch2.6'],
    ['npc_bao', 'story.ch2.7'],
    ['npc_bao', 'story.ch2.8'],
    [
      'npc_mit',
      'story.ch2.9',
      twoChoices('ch2', 'n10', [{ type: 'friendship', npcId: 'npc_mit', amount: 1 }]),
    ],
    ['npc_lan', 'story.ch2.10'],
    ['npc_boss_cat', 'story.ch2.11'],
    ['narrator', 'story.ch2.12'],
  ]),
  dialogue('dlg_ch3', [
    ['narrator', 'story.ch3.1'],
    ['npc_boss_cat', 'story.ch3.2'],
    ['narrator', 'story.ch3.3'],
    ['narrator', 'story.ch3.4'],
    ['grandma_note', 'story.ch3.5'],
    ['npc_lan', 'story.ch3.6'],
    ['narrator', 'story.ch3.7'],
    ['grandma_letter', 'story.ch3.8', twoChoices('ch3', 'n9')],
    // Ensure-owned: the unlock grant (unl_photo_mode) is the real source of the camera (content-schemas §5.2).
    ['narrator', 'story.ch3.9', { effects: [{ type: 'grantItem', itemId: 'dec_grandma_camera', qty: 1 }] }],
  ]),
  // Shown once, after the first photo (story-dialogue §3.3).
  dialogue('dlg_ch3_first_photo', [['narrator', 'story.ch3.10']]),
  dialogue('dlg_ch4', [
    ['npc_lan', 'story.ch4.1'],
    ['narrator', 'story.ch4.2'],
    ['narrator', 'story.ch4.3'],
    ['npc_lan', 'story.ch4.4'],
    ['npc_lan', 'story.ch4.5'],
    [
      'npc_lan',
      'story.ch4.6',
      {
        choices: [
          {
            textKey: 'story.ch4.c1',
            next: 'n7',
            effects: [{ type: 'friendship', npcId: 'npc_lan', amount: 1 }],
          },
          { textKey: 'story.ch4.c2', next: 'n8' },
        ],
      },
    ],
    ['npc_lan', 'story.ch4.7'],
    ['npc_lan', 'story.ch4.8'],
    ['narrator', 'story.ch4.9'],
    ['grandma_note', 'story.ch4.10'],
    ['narrator', 'story.ch4.11', { effects: [{ type: 'grantItem', itemId: 'dec_grandma_photo', qty: 1 }] }],
  ]),
  dialogue('dlg_ch5', [
    ['narrator', 'story.ch5.1'],
    ['grandma_letter', 'story.ch5.2'],
    ['grandma_letter', 'story.ch5.3'],
    ['npc_lan', 'story.ch5.4'],
    ['npc_bao', 'story.ch5.5'],
    ['npc_mit', 'story.ch5.6'],
    ['npc_boss_cat', 'story.ch5.7'],
    ['narrator', 'story.ch5.8', twoChoices('ch5', 'n9')],
    ['narrator', 'story.ch5.9'],
    ['narrator', 'story.ch5.10'],
  ]),
  // ---- Studio NANO (studio-nano.md §7) ----
  dialogue('dlg_ch6', [
    ['narrator', 'story.ch6.1'],
    ['npc_boss_cat', 'story.ch6.2'],
    ['narrator', 'story.ch6.3'],
    ['narrator', 'story.ch6.4'],
    ['grandma_note', 'story.ch6.5'],
    ['npc_lan', 'story.ch6.6'],
    ['npc_lan', 'story.ch6.7'],
    ['npc_bao', 'story.ch6.8'],
    [
      'npc_mit',
      'story.ch6.9',
      twoChoices('ch6', 'n10', [{ type: 'friendship', npcId: 'npc_mit', amount: 1 }]),
    ],
    ['narrator', 'story.ch6.10'],
    ['narrator', 'story.ch6.11'],
  ]),
  dialogue('dlg_ch7', [
    ['narrator', 'story.ch7.1'],
    ['npc_lan', 'story.ch7.2'],
    ['narrator', 'story.ch7.3'],
    ['narrator', 'story.ch7.4'],
    ['npc_lan', 'story.ch7.5'],
    ['npc_bao', 'story.ch7.6'],
    ['npc_mit', 'story.ch7.7'],
    ['narrator', 'story.ch7.8'],
    ['grandma_letter', 'story.ch7.9'],
    ['grandma_letter', 'story.ch7.10', twoChoices('ch7', 'n11')],
    ['narrator', 'story.ch7.11'],
  ]),
  dialogue('dlg_ch8', [
    ['narrator', 'story.ch8.1'],
    ['grandma_letter', 'story.ch8.2'],
    ['grandma_letter', 'story.ch8.3'],
    ['narrator', 'story.ch8.4'],
    ['npc_lan', 'story.ch8.5'],
    ['npc_bao', 'story.ch8.6'],
    ['npc_mit', 'story.ch8.7'],
    ['npc_boss_cat', 'story.ch8.8'],
    ['narrator', 'story.ch8.9'],
    ['narrator', 'story.ch8.10', twoChoices('ch8', 'n11')],
    ['narrator', 'story.ch8.11'],
    ['narrator', 'story.ch8.12'],
    ['narrator', 'story.ch8.13'],
  ]),
];

// ---- Cô Lan (story-dialogue §7.1) ---------------------------------------------------------------------------

const LAN_DIALOGUES: DialogueDefInput[] = [
  ...[0, 1, 2, 3].flatMap((lv) => npcLines('npc_lan', `greet_lv${String(lv)}`, 3)),
  ...npcLines('npc_lan', 'idle', 9),
  ...npcLines('npc_lan', 'daily', 5, [3]),
  // story-dialogue §11.3: one choice gives +1 friendship, plus a bonus line from Lv2.
  {
    id: 'dlg_npc_lan_daily_3',
    nodes: (
      [
        {
          id: 'n1',
          speaker: 'npc_lan',
          textKey: 'npc.npc_lan.daily.3',
          choices: [
            {
              textKey: 'npc.npc_lan.daily.c1',
              next: 'n2',
              effects: [{ type: 'friendship', npcId: 'npc_lan', amount: 1 }],
            },
            { textKey: 'npc.npc_lan.daily.c2', next: 'n3' },
          ],
        },
        { id: 'n2', speaker: 'npc_lan', textKey: 'npc.npc_lan.daily.r1', next: 'n4' },
        { id: 'n3', speaker: 'npc_lan', textKey: 'npc.npc_lan.daily.r2', next: 'n4' },
        {
          id: 'n4',
          speaker: 'npc_lan',
          textKey: 'npc.npc_lan.greet_lv2.1',
          condition: { type: 'friendshipAtLeast', npcId: 'npc_lan', level: 2 },
        },
      ] satisfies Node[]
    ).map(withExpression),
  },
  dialogue('dlg_npc_lan_lvup1', [
    ['npc_lan', 'npc.npc_lan.lvup1.1'],
    ['npc_lan', 'npc.npc_lan.lvup1.2'],
    ['narrator', 'npc.npc_lan.lvup1.3'],
  ]),
  dialogue('dlg_npc_lan_lvup2', [
    ['npc_lan', 'npc.npc_lan.lvup2.1'],
    ['npc_lan', 'npc.npc_lan.lvup2.2'],
    ['narrator', 'npc.npc_lan.lvup2.3'],
  ]),
  dialogue('dlg_npc_lan_lvup3', [
    ['npc_lan', 'npc.npc_lan.lvup3.1'],
    ['npc_lan', 'npc.npc_lan.lvup3.2'],
    ['npc_lan', 'npc.npc_lan.lvup3.3'],
    ['narrator', 'npc.npc_lan.lvup3.4'],
  ]),
  // First visit (Home Coziness Lv2), then lines 5–6 on later visits.
  dialogue('dlg_npc_lan_visit_home_first', [
    ['npc_lan', 'npc.npc_lan.visit_home.1'],
    ['npc_lan', 'npc.npc_lan.visit_home.2'],
    ['npc_lan', 'npc.npc_lan.visit_home.3'],
    ['narrator', 'npc.npc_lan.visit_home.4'],
  ]),
  ...npcLines('npc_lan', 'visit_home', 6, [1, 2, 3, 4]),
  ...npcLines('npc_lan', 'like_place', 3),
];

// ---- Anh Bảo (story-dialogue §7.2) — no home visit by design --------------------------------------------------

const BAO_DIALOGUES: DialogueDefInput[] = [
  ...[0, 1, 2, 3].flatMap((lv) => npcLines('npc_bao', `greet_lv${String(lv)}`, 3)),
  ...npcLines('npc_bao', 'idle', 9),
  ...npcLines('npc_bao', 'daily', 5),
  dialogue('dlg_npc_bao_lvup1', [
    ['npc_bao', 'npc.npc_bao.lvup1.1'],
    ['npc_bao', 'npc.npc_bao.lvup1.2'],
    ['narrator', 'npc.npc_bao.lvup1.3'],
  ]),
  dialogue('dlg_npc_bao_lvup2', [
    ['npc_bao', 'npc.npc_bao.lvup2.1'],
    ['npc_bao', 'npc.npc_bao.lvup2.2'],
    ['narrator', 'npc.npc_bao.lvup2.3'],
  ]),
  dialogue('dlg_npc_bao_lvup3', [
    ['npc_bao', 'npc.npc_bao.lvup3.1'],
    ['npc_bao', 'npc.npc_bao.lvup3.2'],
    ['npc_bao', 'npc.npc_bao.lvup3.3'],
    ['narrator', 'npc.npc_bao.lvup3.4'],
  ]),
  ...npcLines('npc_bao', 'like_place', 3),
];

// ---- Bé Mít (story-dialogue §7.3) -----------------------------------------------------------------------------

const MIT_DIALOGUES: DialogueDefInput[] = [
  ...[0, 1, 2, 3].flatMap((lv) => npcLines('npc_mit', `greet_lv${String(lv)}`, 3)),
  ...npcLines('npc_mit', 'idle', 9),
  ...npcLines('npc_mit', 'daily', 5),
  dialogue('dlg_npc_mit_lvup1', [
    ['npc_mit', 'npc.npc_mit.lvup1.1'],
    ['npc_mit', 'npc.npc_mit.lvup1.2'],
    // .3 once Photo mode is open, otherwise .4 (flag_photo_mode is granted by unl_photo_mode).
    ['narrator', 'npc.npc_mit.lvup1.3', { condition: { type: 'unlocked', unlockId: 'unl_photo_mode' } }],
    [
      'narrator',
      'npc.npc_mit.lvup1.4',
      { condition: { type: 'flag', flag: 'flag_photo_mode', value: false } },
    ],
  ]),
  dialogue('dlg_npc_mit_lvup2', [
    ['npc_mit', 'npc.npc_mit.lvup2.1'],
    ['npc_mit', 'npc.npc_mit.lvup2.2'],
    ['narrator', 'npc.npc_mit.lvup2.3'],
  ]),
  dialogue('dlg_npc_mit_lvup3', [
    ['npc_mit', 'npc.npc_mit.lvup3.1'],
    ['npc_mit', 'npc.npc_mit.lvup3.2'],
    ['npc_mit', 'npc.npc_mit.lvup3.3'],
    ['narrator', 'npc.npc_mit.lvup3.4'],
  ]),
  // First visit (Home Coziness Lv4, together with Boss Cat), then lines 7–8 on later visits.
  dialogue('dlg_npc_mit_visit_home_first', [
    ['npc_mit', 'npc.npc_mit.visit_home.1'],
    ['npc_boss_cat', 'npc.npc_mit.visit_home.2'],
    ['narrator', 'npc.npc_mit.visit_home.3'],
    ['npc_mit', 'npc.npc_mit.visit_home.4'],
    ['npc_mit', 'npc.npc_mit.visit_home.5'],
    ['narrator', 'npc.npc_mit.visit_home.6'],
  ]),
  ...npcLines('npc_mit', 'visit_home', 8, [1, 2, 3, 4, 5, 6]),
  ...npcLines('npc_mit', 'like_place', 3),
];

// ---- Sếp Mèo (story-dialogue §8) ------------------------------------------------------------------------------

const CAT_DIALOGUES: DialogueDefInput[] = [
  // 1–12: random taps; 13: once the daily petting bonus is used up.
  ...range(13).map((n) => catWithCaption(`dlg_cat_tap_${String(n)}`, 'tap', n)),
  ...range(4).map((n) => catNarration(`dlg_cat_block_${String(n)}`, `cat.block.${String(n)}`)),
  ...range(3).map((n) => catWithCaption(`dlg_cat_unblock_${String(n)}`, 'unblock', n)),
  catNarration('dlg_cat_pate_1', 'cat.pate.1'),
  catWithCaption('dlg_cat_pate_2', 'pate', 2),
  dialogue('dlg_cat_lvup1', [
    ['narrator', 'cat.lvup1.1'],
    ['npc_boss_cat', 'cat.lvup1.2'],
    ['narrator', 'cat.lvup1.3'],
    ['narrator', 'cat.lvup1.4'],
  ]),
  dialogue('dlg_cat_lvup2', [
    ['narrator', 'cat.lvup2.1'],
    ['npc_boss_cat', 'cat.lvup2.2'],
    ['narrator', 'cat.lvup2.3'],
    ['narrator', 'cat.lvup2.4'],
  ]),
  // Plays on the 20th pet, when ach_cat_boss completes.
  dialogue('dlg_cat_ach_secret', [
    ['npc_boss_cat', 'cat.ach_secret.1'],
    ['narrator', 'cat.ach_secret.2'],
    ['narrator', 'cat.ach_secret.3'],
  ]),
  catNarration('dlg_cat_boss_key_1', 'cat.boss_key.1'),
  catNarration('dlg_cat_boss_key_2', 'cat.boss_key.2'),
];

export const DIALOGUES: readonly DialogueDefInput[] = [
  ...CHAPTER_DIALOGUES,
  ...LAN_DIALOGUES,
  ...BAO_DIALOGUES,
  ...MIT_DIALOGUES,
  ...CAT_DIALOGUES,
];
